import React, { useState, useEffect } from 'react';
import {  ArrowDown, ArrowRightCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import WhatsAppCommunityBanner from '../components/utilities/WhatsAppCommunityBanner';

const AbusePage: React.FC = () => {
    const [currentText, setCurrentText] = useState("Healing");
    const textCycle = ["Healing", "Empowering", "Recovering", "Transforming"];

    const navigate = useNavigate();

    useEffect(() => {
        const interval = setInterval(() => {
            const currentIndex = textCycle.indexOf(currentText);
            const nextIndex = (currentIndex + 1) % textCycle.length;
            setCurrentText(textCycle[nextIndex]);
        }, 2000);

        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, [currentText]);

    const typesCards = [
        {

            title: "Physical Abuse",
            description: "When someone uses force to hurt, injure, or cause pain to another person. It can cause physical injuries as well as emotional distress and fear.",
        },
        {

            title: "Emotional or Psychological Abuse",
            description: "When a person's mental and emotional well-being is targeted. It may involve use of actions or words to control, manipulate, blame, humiliate, or criticise someone, which can hurt how they feel about themselves.",
        },
        {

            title: "Sexual Abuse",
            description: "It involves non-consensual (meaning someone didn't agree to it) sexual activity. It can be even non-physical like exposing someone to pornography. It's exploitative, harmful and controlling.",
        },
        {
            title: "Neglect",
            description: "When someone intentionally refuses or fails to provide proper care, attention, support, or protection to another person. It can mean not providing basic needs like food, shelter, hygiene, or medical care, or not providing enough supervision. Neglect often doesn't happen by itself; it's common to see other types of maltreatment happening too."
        },
        {
            title: "Domestic Violence",
            description: "Also called domestic abuse, this is a pattern of abusive behaviour that happens in a close relationship, like between partners. It's used to gain control and power over the other person and can include emotional, physical, financial, or sexual abuse."
        }
        ,
        {
            title: "Financial Abuse",
            description: "This involves controlling, exploiting, or restricting someone's access to their money or financial resources."
        },
        {
            title: "Organisational Abuse",
            description: "This happens in organisations like workplaces, schools, or care homes where harmful or unethical practices, mistreatment, or lack of support occur. It can also be due to bad policies, abuse of power or neglect of safety."
        },
        {
            title: "Discrimination",
            description: "It is unfair or prejudiced treatment of individuals or groups based on things they do not have control over such as their race, sex, religion, age, disability, or sexual orientation. It can deny people basic rights and create disadvantages."
        },
        {
            title: "Bullying",
            description: "This is when a person or group repeatedly and intentionally harms someone considered weaker. It can happen in many places like schools, workplaces, or even families."
        },
        {
            title: "Modern Slavery",
            description: "This refers to different kinds of exploitation and forced labour that still happen, often involving threats, violence, or abuse of power."
        }
    ];

    const effectCards = [
        {
            title: "Mental Health Problems",
            description: "Increased risk of developing conditions like depression, anxiety, and PTSD (Post-Traumatic Stress Disorder). Symptoms of PTSD can include reoccurring thoughts or flashbacks of the event, avoiding things that remind you of the trauma, emotional numbness, irritability, trouble sleeping or concentrating, and intense emotional reactions. Other issues include panic attacks, and emotional distress."
        },
        {
            title: "Difficulty with Relationships",
            description: "Difficulty in forming and keeping relationships, and you might struggle with trust. It can disrupt the relationship a child has with their caregiver, making them less secure."
        },
        {
            title: "Low Self-Esteem and Self-Worth",
            description: "Low self-esteem and a diminished sense of self-worth, guilt, or shame. Emotional abuse, in particular, can impact how you feel about yourself and how valuable you think you are."
        },
        {
            title: "Coping Difficulties",
            description: "Struggles with managing emotions, leading to intense feelings of anger, sadness, or anxiety. Survivors might turn to substance abuse (like drugs or alcohol) or other self-destructive behaviours as a way to cope."
        },
        {
            title: "Physical Health",
            description: "It’s been established that early trauma can disrupt the body's stress response system, which can lead to changes in how the body handles stress. It may also increase the risk for physical illnesses later on. For girls, sexual abuse and neglect have been linked to different patterns of weight gain in adolescence compared to girls who weren&t abused. Sexual abuse in girls has also been linked to earlier puberty."
        },
        {
            title: "Brain Changes",
            description: "Influence on the structure and function of the brain. Areas of the brain important for memory, learning, executive functions (like impulse control), emotional regulation, anxiety, and stress can be affected. Specific areas like the amygdala (involved in fear), hippocampus (memory), and prefrontal cortex (emotional regulation) can change. Amygdala activity might increase, making someone more sensitive to threats."
        },
        {
            title: "Behavioural Problems",
            description: "Abuse can lead to acting-out behaviours, including age-inappropriate sexual behaviours, delinquency, and substance use. These issues can continue into adulthood."
        },
    ]

    return (
        <>

            <Helmet>
                {/* Basic Meta Tags */}
                <title>Psychologist for Abuse Therapy &amp; PTSD | Trauma Support</title>
                <meta name="description" content="Find expert psychologists to tackle lasting psychological effects of abuse, including emotional,
physical, sexual, and domestic violence. Get Support Now." />"



                {/* Sensitive Content Warning */}
                <meta name="rating" content="mature" />

                {/* Robots Instruction */}
                <meta name="robots" content="index, follow" />

                {/* Open Graph / Social Media Tags */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content="Psychologist for Abuse Therapy &amp; PTSD | Trauma Support" />
                <meta
                    property="og:description"
                    content="Find expert psychologists to tackle lasting psychological effects of abuse, including emotional,
physical, sexual, and domestic violence. Get Support Now."
                />
                <meta property="og:url" content="https://grehpraveshtherapy.com/abuse" />
                <meta property="og:image" content="/images/abuse-support.png" />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Survivor Support - Your Journey to Healing" />
                <meta
                    name="twitter:description"
                    content="Compassionate, trauma-informed support for survivors. Every healing journey is unique and valued."
                />
                <meta name="twitter:image" content="/images/abuse-support.png" />

                {/* Keywords */}
                <meta
                    name="keywords"
                    content="abuse survivor support, trauma recovery, healing from abuse, survivor empowerment, safe counseling, emotional healing, trauma-informed care"
                />

                {/* Structured JSON-LD Schema Markup */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "HealthAndWellnessProfessional",
                        "name": "Survivor Support Services",
                        "description": "Comprehensive, compassionate support for survivors of abuse, focusing on healing, empowerment, and personal transformation.",
                        "offers": {
                            "@type": "Offer",
                            "description": "Professional, survivor-centered counseling and support services"
                        },
                        "serviceType": [
                            "Trauma Recovery",
                            "Survivor Support",
                            "Emotional Healing"
                        ],
                        "areaServed": {
                            "@type": "GeoShape",
                            "additionalType": "Service Area"
                        },
                        "hasCredential": {
                            "@type": "EducationalOccupationalCredential",
                            "name": "Trauma-Informed Counseling Professionals"
                        },
                        "additionalType": [
                            "Survivor Support",
                            "Trauma Counseling",
                            "Emotional Wellness"
                        ]
                    })}
                </script>
            </Helmet>
            <div className="bg-white">
                {/* Landing Section */}
                <section className="relative min-h-screen flex flex-col md:flex-row overflow-hidden bg-gradient-to-br from-earthybeige to-golden-yellow">
                    {/* Background layers */}
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-purple/70 to-purple/30 rounded-br-[100%] z-0"></div>
                    <div className="absolute top-0 right-0 w-full h-full bg-contain bg-no-repeat bg-right-top opacity-10 md:opacity-20 z-0" style={{ backgroundImage: "url('/images/abuse-support.png')" }}></div>

                    <div className="w-full md:w-7/12 z-10 flex items-center text-center mt-28 md:mt-0 text-center px-6 md:pl-24 md:pr-0 relative">
                        <div className="max-w-2xl w-full space-y-6 md:mt-20">




                            <motion.h1
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.9 }}
                                className="text-4xl md:text-4xl text-white font-semibold md:font-bold mt-32 md:mt-0"
                            >
                                Therapy for Abuse
                            </motion.h1>





                            <motion.div
                                className="pt-8 space-y-6"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 2.7 }}
                            >
                                <button
                                    onClick={() => navigate('/book-appointment')}
                                    className="bg-white text-earthybeige flex items-center justify-center text-center px-6 py-3 text-sm md:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all w-full md:w-auto mx-auto">
                                    Start Your Healing Journey
                                    <ArrowRightCircle className="ml-3 w-7 h-7 text-earthybeige" />
                                </button>

                                {/* Mobile Image Section */}
                                <motion.div
                                    className="block md:hidden w-full h-96 md:h-full"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.9, delay: 3 }}
                                >
                                    <img
                                        src="/images/abuse-support.png"
                                        alt="abuse support"
                                        className="w-full h-full object-cover rounded-xl opacity-80"
                                    />
                                </motion.div>
                            </motion.div>
                        </div>
                    </div>

                    {/* Desktop Image Section */}
                    <motion.div
                        className="hidden md:block absolute top-0 right-0 w-5/12 h-full z-0"
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.9, delay: 3 }}
                    >
                        <img
                            src="/images/abuse-support.png"
                            alt="abuse support"
                            className="absolute inset-0 w-full h-full object-cover opacity-70"
                        />
                    </motion.div>
                </section>

                {/* Scroll Indicator */}
                <div className="text-center py-4 bg-white">
                    <ArrowDown
                        className="animate-bounce mx-auto text-purple"
                        size={32}
                    />
                </div>

                <div className="container mx-auto px-4 py-12">
                    <section className="max-w-6xl mx-auto mb-16 lg:mb-20">
                        <div className="text-center mb-8 lg:mb-12">
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-semibold text-gray-800 mb-6 leading-tight">
                                What is Abuse?
                            </h2>
                        </div>
                        <div className="space-y-6 lg:space-y-8 max-w-4xl mx-auto">
                            <div className="bg-gradient-to-r from-violet-50 to-yellow-50 rounded-xl p-6 lg:p-8">
                                <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed">
                                    Abuse is when a person causes harm, injury, or distress to another person through words, actions or
                                    behaviour. This harm could be to someone's body (physical) or their mind and feelings (psychological
                                    or mental). Mostly, it is not just a one-time event; it often involves a pattern of words and/or actions
                                    over time.
                                </p>
                            </div>
                        </div>
                    </section>

                    <section className="max-w-6xl mx-auto mb-16 lg:mb-20">
                        <div className="text-center mb-8 lg:mb-12">
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-semibold text-gray-800 mb-6 leading-tight">
                                Abuse is Not the Victim's Fault
                            </h2>
                        </div>
                        <div className="space-y-6 lg:space-y-8 max-w-4xl mx-auto">
                            <div className="bg-red-50 border-l-4 border-red-400 rounded-r-xl p-6 lg:p-8">
                                <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed">
                                    People who abuse others are often in a position where they have power over the other person, or
                                    they take advantage of a close or special relationship. Therefore, it's really important to remember
                                    this that it's never the fault of the victim.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Different Types of Abuse */}
                    <section className="max-w-6xl mx-auto mb-16 lg:mb-20">
                        <div className="text-center mb-8 lg:mb-12">
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-semibold text-gray-800 mb-6 leading-tight">
                                Different Types of Abuse
                            </h2>
                        </div>
                        <div className="space-y-6 lg:space-y-8 max-w-4xl mx-auto mb-8">
                            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 lg:p-8">
                                <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed">
                                    Abuse comes in many different forms. These are the most common types of abuse:
                                </p>
                            </div>
                        </div>

                        <div className="grid gap-6 lg:gap-8 sm:grid-cols-2 lg:grid-cols-3 mb-8">
                            {typesCards.map((card, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.2 }}
                                    className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all hover:scale-105 hover:shadow-xl"
                                >
                                    <div className="p-6 lg:p-8">
                                        <h3 className="text-xl lg:text-2xl font-semibold text-gray-800 text-center mb-4">
                                            {card.title}
                                        </h3>
                                        <p className="text-center text-base lg:text-lg text-gray-600 leading-relaxed">
                                            {card.description}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        <div className="max-w-4xl mx-auto">
                            <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-r-xl p-6 lg:p-8">
                                <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed">
                                    It's important to know that experiencing just one type of maltreatment is uncommon. Often,
                                    different types happen at the same time (this is called co-occurrence). For example, neglect rarely
                                    happens on its own.
                                </p>
                            </div>
                        </div>
                    </section>



                    <section className="max-w-6xl mx-auto mb-16 lg:mb-20">
                        <div className="text-center mb-8 lg:mb-12">
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-semibold text-gray-800 mb-6 leading-tight">
                                Who Can Be Affected?
                            </h2>
                        </div>
                        <div className="space-y-6 lg:space-y-8 max-w-4xl mx-auto">
                            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 lg:p-8">
                                <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed">
                                    Abuse can happen to anyone, at any age. It can occur in romantic relationships, friendships, and
                                    even parent-child relationships. Children, the elderly, people with disabilities, or those with severe
                                    mental health problems can be especially vulnerable. It's not limited to certain groups based on
                                    income or education.
                                </p>
                            </div>
                        </div>
                    </section>

                    <section className="max-w-6xl mx-auto mb-16 lg:mb-20">
                        <div className="text-center mb-8 lg:mb-12">
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-semibold text-gray-800 mb-6 leading-tight">
                                Why is it Hard to Recognize or Leave Abuse?
                            </h2>
                        </div>
                        <div className="space-y-6 lg:space-y-8 max-w-4xl mx-auto">
                            <div className="bg-orange-50 rounded-xl p-6 lg:p-8 border border-orange-200">
                                <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed">
                                    Recognising abuse can be difficult, especially if someone has lived with it for a long time. It might
                                    make a person think that how they are being treated is normal or the "right" way. Abusers also use
                                    different methods to make it hard for people to leave, such as making them financially dependent,
                                    using children, threatening violence, or using legal issues.
                                </p>
                            </div>
                        </div>
                    </section>

                    <section className="max-w-6xl mx-auto mb-16 lg:mb-20">
                        <div className="text-center mb-8 lg:mb-12">
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-semibold text-gray-800 mb-6 leading-tight">
                                Why Do People Become Abusers?
                            </h2>
                        </div>
                        <div className="space-y-6 lg:space-y-8 max-w-4xl mx-auto">
                            <div className="grid gap-6 lg:gap-8 md:grid-cols-1">
                                <div className="bg-white rounded-xl p-6 lg:p-8 shadow-lg border border-gray-100">
                                    <h3 className="text-lg lg:text-xl font-semibold text-gray-800 mb-4">Power and Control</h3>
                                    <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                                        The causes of emotional abuse, for example, are complex, but some factors mentioned include the
                                        abuser wanting power and control, especially in relationships where one person has more power.
                                        Abusive behaviour can be something people learn from their families or others.
                                    </p>
                                </div>

                                <div className="bg-white rounded-xl p-6 lg:p-8 shadow-lg border border-gray-100">
                                    <h3 className="text-lg lg:text-xl font-semibold text-gray-800 mb-4">Mental Health & Past Trauma</h3>
                                    <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                                        Mental health issues, personality disorders such as narcissistic or borderline personality disorder, or
                                        past trauma like being abused or neglected as a child can also make someone more likely to be
                                        abusive. Substance abuse can also contribute. Sometimes, cultural or societal factors play a role.
                                    </p>
                                </div>

                                <div className="bg-white rounded-xl p-6 lg:p-8 shadow-lg border border-gray-100">
                                    <h3 className="text-lg lg:text-xl font-semibold text-gray-800 mb-4">Underlying Issues</h3>
                                    <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                                        A lack of empathy or low self-esteem in the abuser can also be factors. Some experts think that
                                        unresolved childhood trauma might be a main cause of people psychologically harming others, and
                                        the abuser may be suffering themselves but not know it.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="max-w-6xl mx-auto mb-16 lg:mb-20">
                        <div className="text-center mb-8 lg:mb-12">
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-semibold text-gray-800 mb-6 leading-tight">
                                The Effects of Abuse
                            </h2>
                        </div>
                        <div className="space-y-6 lg:space-y-8 max-w-4xl mx-auto mb-8">
                            <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-xl p-6 lg:p-8">
                                <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed">
                                    Abuse can have serious and long-lasting effects on people, both physically and psychologically. It can
                                    be just as damaging as physical abuse. Here are some effects:
                                </p>
                            </div>
                        </div>

                        <div className="grid gap-6 lg:gap-8 sm:grid-cols-2 lg:grid-cols-3 mb-8">
                            {effectCards.map((card, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.2 }}
                                    className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all hover:scale-105 hover:shadow-xl"
                                >
                                    <div className="p-6 lg:p-8">
                                        <h3 className="text-xl lg:text-2xl font-semibold text-gray-800 text-center mb-4">
                                            {card.title}
                                        </h3>
                                        <p className="text-center text-base lg:text-lg text-gray-600 leading-relaxed">
                                            {card.description}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        <div className="max-w-4xl mx-auto">
                            <div className="bg-blue-50 border-l-4 border-blue-400 rounded-r-xl p-6 lg:p-8">
                                <p className="text-base sm:text-lg lg:text-xl text-gray-700 leading-relaxed">
                                    Research shows that experiencing maltreatment, even beyond just living in a difficult
                                    neighbourhood, adds an extra risk for developing mental health problems.
                                </p>
                            </div>
                        </div>
                    </section>

                    <section className="mb-16 lg:mb-20 bg-white rounded-xl shadow-lg overflow-hidden">
                        <div className="grid lg:grid-cols-2 gap-0">
                            <div className="bg-gray-50 p-6 lg:p-8 flex flex-col justify-center">
                                <h2 className="text-2xl lg:text-3xl font-semibold text-gray-800 mb-6">
                                    Getting Help and Treatment
                                </h2>
                                <div className="space-y-4">
                                    <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                                        Overcoming abuse can be difficult to do on your own. Seeking professional help is highly
                                        recommended. Therapy or counselling provides a safe and confidential space to cope with the issues
                                        caused by abuse.
                                    </p>
                                    <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                                        Therapy can help you explore whether what you're experiencing is abuse and assist you in taking
                                        steps to end an abusive relationship. Persistent efforts can help you escape feelings of helplessness,
                                        restore self-esteem, learn healthy coping strategies, and help you trust yourself and others again.
                                    </p>
                                    <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                                        For those currently in an emotionally abusive relationship, having a counsellor can be a safe way to
                                        help prevent the abuse from escalating.
                                    </p>
                                </div>
                            </div>

                            <div className="p-6 lg:p-8 space-y-6">
                                <div className="bg-blue-50 rounded-xl p-6 shadow-md">
                                    <h3 className="text-lg lg:text-xl font-semibold text-center text-gray-800 mb-4">
                                        Types of therapy for abuse-related mental health issues include:
                                    </h3>
                                    <ol className="list-decimal list-inside text-sm lg:text-base text-gray-600 space-y-2">
                                        <li>Cognitive Behavioural Therapy (CBT)</li>
                                        <li>Eye Movement Desensitisation and Reprocessing (EMDR)</li>
                                        <li>Trauma-Focused Cognitive Behavioural Therapy (TF-CBT), designed for children affected by sexual
                                            abuse or trauma. It helps children and parents process traumatic experiences and learn coping skills.</li>
                                        <li>Compassion Focused Therapy (CFT)</li>
                                        <li>Dialectical Behaviour Therapy (DBT)</li>
                                        <li>Emotion Focused Therapy (EFT)</li>
                                        <li>Counselling & Psychotherapy</li>
                                        <li>Family Therapy, Couples Therapy, and Group Therapy can also be helpful.</li>
                                        <li>Person-Centred Therapy and Interpersonal Therapy (IPT) are also mentioned.</li>
                                    </ol>
                                </div>

                                <div className="bg-green-50 rounded-xl p-6 shadow-md">
                                    <h3 className="text-lg lg:text-xl font-semibold text-center text-gray-800 mb-4">
                                        Things you can do to cope:
                                    </h3>
                                    <ul className="list-disc list-inside text-sm lg:text-base text-gray-600 space-y-2">
                                        <li>Acknowledge the abuse and understand triggers and coping mechanisms.</li>
                                        <li>Engage in physical activities like yoga and meditation to feel more centered and enhance self-esteem.</li>
                                        <li>Reach out for help from trusted people, which can provide emotional strength.</li>
                                        <li>Practice self-compassion and be kind to yourself.</li>
                                        <li>Learn about healthy relationships and boundaries to recognise signs of abuse and set limits.</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section>
                    <section className="mb-16 lg:mb-20 bg-white rounded-xl shadow-lg overflow-hidden">
                        <div className="bg-amber-50 p-6 lg:p-8">
                            <h2 className="text-2xl lg:text-3xl font-semibold text-gray-800 mb-6">
                                Cultural Factors
                            </h2>
                            <div className="space-y-4 max-w-4xl">
                                <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                                    Cultural factors can sometimes make it harder for people to report abuse. In some cultures, ideas
                                    like male dominance and norms that discourage speaking out can lead to victims being blamed or
                                    rejected when they report abuse.
                                </p>
                                <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                                    Understanding these cultural differences is important, especially for those in legal or law
                                    enforcement roles, to provide proper support. Providers should also be sensitive to the different
                                    cultural backgrounds of their clients.
                                </p>
                            </div>
                        </div>
                    </section>
                    <section className="mb-16 lg:mb-20 bg-white rounded-xl shadow-lg overflow-hidden">
                        <div className="grid lg:grid-cols-2 gap-0">
                            <div className="bg-teal-50 p-6 lg:p-8 flex flex-col justify-center">
                                <h2 className="text-2xl lg:text-3xl font-semibold text-gray-800 mb-6">
                                    Trauma-Informed Care
                                </h2>
                                <p className="text-base lg:text-lg text-gray-600 leading-relaxed">
                                    Because trauma, often from abuse, has widespread and lasting effects, many services are trying to
                                    become "trauma-informed". This means understanding that trauma is common and affects people's
                                    health and behaviour. A trauma-informed approach involves:
                                </p>
                            </div>

                            <div className="p-6 lg:p-8 space-y-6">
                                <div className="bg-teal-50 rounded-xl p-6 shadow-md">
                                    <ol className="list-decimal list-inside text-sm lg:text-base text-gray-600 space-y-3">
                                        <li>Understanding the wide impact of trauma and how people can recover.</li>
                                        <li>Recognising the signs of trauma in individuals, families, and staff.</li>
                                        <li>Using knowledge about trauma in policies and practices.</li>
                                        <li>Trying to avoid accidentally re-traumatizing people. This means not doing things that might
                                            remind someone of their traumatic experiences and cause them distress.</li>
                                    </ol>
                                </div>

                                <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
                                    <div className="space-y-4">
                                        <p className="text-sm lg:text-base text-gray-600 leading-relaxed">
                                            A trauma-informed approach asks "what happened to you?" Instead of asking "what's wrong with
                                            you?", while still holding people responsible for their actions.
                                        </p>
                                        <p className="text-sm lg:text-base text-gray-600 leading-relaxed">
                                            Overall, abuse is a complex issue with many forms and serious effects. However, with the right
                                            support and therapeutic approaches, healing and recovery are possible.
                                        </p>
                                        <p className="text-sm lg:text-base text-gray-600 leading-relaxed">
                                            If you're looking to talk to someone, feel free to reach out by clicking on 'schedule consultation'
                                            below or just give us a call and our experts will guide you.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>



                    {/* Testimonial Section */}
                    <section className="mt-16 bg-earthybeige bg-opacity-10 rounded-xl p-8">
                        <h2 className="text-3xl font-gentium-book-plus-regular text-charcol text-center mb-8">
                            Survivor Stories
                        </h2>
                        <div className="grid md:grid-cols-3 gap-6">
                            {[
                                {
                                    quote: "Healing isn't linear, but with support, I've learned that my strength is greater than my pain. Each day, I reclaim a piece of myself.",
                                    image: "/images/avatar5.png"
                                },
                                {
                                    quote: "I discovered that survival isn't just about enduring—it's about thriving. My journey has transformed my wound into wisdom.",
                                    image: "/images/avatar3.png"
                                },
                                {
                                    quote: "Breaking silence was my first act of courage. Now, I stand not as a victim, but as a survivor—powerful, resilient, and unbroken.",
                                    image: "/images/avatar1.png"
                                }
                            ].map((story, index) => (
                                <div
                                    key={index}
                                    className="bg-white rounded-xl p-6 shadow-md relative"
                                >
                                    <img
                                        src={story.image}
                                        alt="Survivor Testimonial"
                                        className="w-16 h-16 rounded-full absolute -top-8 left-1/2 transform -translate-x-1/2 border-4 border-white shadow-md"
                                    />
                                    <div className="text-center mt-8">
                                        <p className="font-lato-regular text-sage italic mb-4">
                                            "{story.quote}"
                                        </p>
                                        <h3 className="font-raleway-regular text-charcol">
                                            Anonymous Survivor
                                        </h3>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                    <WhatsAppCommunityBanner />

                    {/* Call to Action */}
                    <section className="mt-16 bg-golden-yellow bg-opacity-10 rounded-xl p-12 text-center">
                        <h2 className="text-3xl font-gentium-book-plus-regular text-charcol mb-6">
                            You Are Not Alone
                        </h2>
                        <p className="text-xl font-lato-regular text-sage max-w-2xl mx-auto mb-8">
                            Every step towards healing is an act of courage. We are here to support you
                            with compassion, understanding, and unwavering respect.
                        </p>
                        <div className="flex flex-col md:flex-row justify-center space-x-4 md:space-x-4 space-y-4 md:space-y-0 w-full max-w-md mx-auto">
                            <Link
                                to='/book-appointment'
                                className="bg-golden-yellow text-white px-8 py-3 rounded-lg hover:bg-opacity-90 transition-colors font-raleway-regular"
                            >
                                Schedule Consultation
                            </Link>

                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};

export default AbusePage;